import { useEffect, useRef } from 'react';

import { type GeolocationInfo } from '~/vendor/cx-web/api-clients/geoip-api/types';

interface UseGZConsentGatedGTMOptions {
  consentGranted: boolean;
  geolocationInfo: GeolocationInfo | undefined;
  gtmId: string | undefined;
  userGroup?: string;
  userId?: string;
}

/** Loads GTM only after consent is granted. Idempotent - only loads once. */
export function useGZConsentGatedGTM({
  consentGranted,
  geolocationInfo,
  gtmId,
  userGroup = '',
  userId = '',
}: UseGZConsentGatedGTMOptions): void {
  const hasLoadedRef = useRef(false);

  useEffect(() => {
    if (hasLoadedRef.current) return;

    if (!consentGranted) return;

    if (!gtmId || !geolocationInfo) return;

    hasLoadedRef.current = true;

    const { countryCode, geoCity, geoCountry, geoRegion, subdivisionCode } = geolocationInfo;

    const dataLayer = (window.dataLayer = window.dataLayer || []) as unknown[];
    dataLayer.push({
      country: countryCode,
      countryCode,
      geoCity,
      geoCountry,
      geoRegion,
      repub: 'GZ',
      state: subdivisionCode ?? '',
      userGroup,
      userId,
    });

    dataLayer.push({
      'gtm.start': new Date().getTime(),
      event: 'gtm.js',
    });

    const script = document.createElement('script');
    script.src = `https://www.googletagmanager.com/gtm.js?id=${gtmId}`;
    script.async = true;
    document.head.appendChild(script);
  }, [consentGranted, geolocationInfo, gtmId, userGroup, userId]);
}
